import { type FC } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Box,
} from '@mui/material';
import { Controller } from 'react-hook-form';

import { AuditTypeChip } from '#apps/client-web/features/audit/components/audit-type-chip';
import { AuditTypeIcon } from '#apps/client-web/features/audit/components/audit-type-icon';

import { type NewAuditForm } from './types';

export type AuditCreatePageRendererProps = {
  isPending: boolean;
  form: NewAuditForm;
  onSubmit: () => void;
};

export const AuditCreatePageRenderer: FC<AuditCreatePageRendererProps> = ({
  isPending,
  form,
  onSubmit,
}) => {
  const { control } = form;
  const disableForm = isPending;

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={4} sx={{ mt: 2 }} flex={1}>
        <Controller
          control={control}
          name="title"
          rules={{
            required: { value: true, message: 'Title is required' },
            minLength: 1,
            maxLength: 253,
          }}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              error={fieldState.invalid}
              aria-invalid={fieldState.invalid}>
              <TextField
                {...field}
                autoFocus
                label="Title"
                type="text"
                variant="outlined"
                disabled={disableForm}
                error={fieldState.invalid}
                aria-invalid={fieldState.invalid}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="types"
          rules={{
            required: { value: true, message: 'Type is required' },
            minLength: { value: 1, message: 'Select at least one type' },
          }}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              error={!!fieldState.error}
              aria-invalid={!!fieldState.error}>
              <InputLabel>Type</InputLabel>
              <Select
                disabled={disableForm}
                multiple
                error={fieldState.invalid}
                aria-invalid={fieldState.invalid}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                input={
                  <OutlinedInput
                    label="Type"
                    error={fieldState.invalid}
                    aria-errormessage={fieldState.error?.message}
                  />
                }
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5}>
                    {selected.map((value) => (
                      <AuditTypeChip key={value} type={value} size="medium" />
                    ))}
                  </Stack>
                )}>
                <MenuItem value="ea">
                  <ListItemIcon>
                    <AuditTypeIcon type="ea" />
                  </ListItemIcon>
                  <ListItemText>Energy Audit (EA)</ListItemText>
                </MenuItem>
                <MenuItem value="bca">
                  <ListItemIcon>
                    <AuditTypeIcon type="bca" />
                  </ListItemIcon>
                  <ListItemText>
                    Building Condition Assessment (BCA)
                  </ListItemText>
                </MenuItem>
              </Select>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Stack>
    </Box>
  );
};
